<template>
  <q-dialog
    v-model="shown"
    persistent
  >
    <q-card
      class="relative-position"
      style="min-width: 800px; min-height: 587px; max-height: 90vh;"
    >
      <q-spinner
        v-if="!showing || showing.loading || changeExecuting"
        class="absolute-center text-grey"
        size="lg"
      />
      <template v-else>
        <q-card-section
          ref="cardHeader"
          class="row full-width justify-start q-pb-none"
        >
          <div class="text-h6">
            Modify Showing (Beta) - {{ showing.movie.name }} - {{ showing.siteTime }}
          </div>
        </q-card-section>
        <q-card-section
          :style="`max-height: calc(90vh - ${headerHeight});`"
          class="full-width q-ma-none q-pa-none"
        >
          <q-stepper
            ref="stepper"
            v-model="step"
            class="full-width"
            color="primary"
            animated
          >
            <q-step
              :name="1"
              title="Configure"
              icon="settings"
              :done="step > 1"
              style="min-height: 400px;"
            >
              <div
                class="row q-col-gutter-lg"
              >
                <q-select
                  v-model="showing.action"
                  class="col-6"
                  label="Change Type"
                  :options="showing.fieldOptions('action')"
                  :rules="[val => !!val || 'Selection is required']"
                  map-options
                  emit-value
                  bottom-slots
                />
              </div>
              <div
                v-if="showing.action === SHOWING_CHANGE_ACTIONS.SCREEN_SWAP && seatSwapsRequired"
                class="row q-col-gutter-lg q-mb-lg"
              >
                <div class="col-6 column">
                  <q-item-label class="q-ml-xs text-uppercase">
                    Swap Strategy
                  </q-item-label>
                  <q-btn-toggle
                    v-model="swapStrategy"
                    size="md"
                    class="q-my-xs row"
                    outline
                    spread
                    toggle-color="primary"
                    :options="$mh.asOptions(SWAP_STRATEGIES.ALL)"
                  >
                    <template #magic>
                      <q-tooltip>
                        Swaps seats based on an intelligent combination of the
                        user's original seat selection and their historical seat
                        preferences.
                      </q-tooltip>
                    </template>

                    <template #originalSeats>
                      <q-tooltip>
                        Swaps seats based on the user's original seat selection,
                        and ignore their historical seat preferences.
                      </q-tooltip>
                    </template>

                    <template #userPreference>
                      <q-tooltip>
                        Swap seats based on the user's historical seat
                        preferences, and ignore their original seat selection.
                      </q-tooltip>
                    </template>
                  </q-btn-toggle>
                </div>

                <div class="col-6 row items-center">
                  <q-toggle
                    v-model="consolidateToCenter"
                    class="col-6"
                    label="Consolidate to Center"
                  >
                    <q-tooltip>
                      Consolidate new seats to the horizontal center of the auditorium.
                    </q-tooltip>
                  </q-toggle>
                  <div
                    v-if="(swapStrategy === SWAP_STRATEGIES.MAGIC || swapStrategy === SWAP_STRATEGIES.ORIGINAL_SEATS)"
                    class="col-6 column"
                  >
                    <q-item-label class="q-ml-xs text-uppercase">
                      Relocation Tolerance
                    </q-item-label>
                    <q-btn-toggle
                      v-model="relocationTolerance"
                      class="q-my-xs"
                      outline
                      spread
                      toggle-color="primary"
                      :options="[
                        { label: '0%', value: 0 },
                        { label: '50%', value: 0.5 },
                        { label: '100%', value: 1 },
                      ]"
                    >
                      <q-tooltip v-if="!consolidateToCenter">
                        The percentage of acceptable horizontal and vertical movement from the original seat positions within the original auditorium.
                      </q-tooltip>
                      <q-tooltip v-else>
                        The percentage of acceptable vertical movement from the original seat positions within the original auditorium.
                      </q-tooltip>
                    </q-btn-toggle>
                  </div>
                </div>
              </div>
              <div
                class="row q-col-gutter-lg items-start"
              >
                <template v-if="showing.action === SHOWING_CHANGE_ACTIONS.RESCHEDULE">
                  <q-input
                    class="col-6"
                    :model-value="showing.siteTime"
                    label="From Date/Time"
                    readonly
                  />
                  <q-input
                    class="col-6"
                    :model-value="date.formatDate(adjustedShowingTime, 'YYYY MMM D, h:mm a')"
                    label="To Date/Time"
                  >
                    <template #prepend>
                      <q-icon
                        v-if="adjustedShowingTime"
                        name="event"
                        class="cursor-pointer"
                      >
                        <q-popup-proxy
                          transition-show="scale"
                          transition-hide="scale"
                        >
                          <q-date
                            :model-value="date.formatDate(adjustedShowingTime, 'YYYY-MM-DDTHH:mm:ssZ')"
                            flat
                            mask="YYYY-MM-DDTHH:mm:ssZ"
                            @update:model-value="value => newTime = date.extractDate(value, 'YYYY-MM-DDTHH:mm:ssZ')"
                          />
                        </q-popup-proxy>
                      </q-icon>
                    </template>
                    <template #append>
                      <q-icon
                        v-if="adjustedShowingTime"
                        name="access_time"
                        class="cursor-pointer"
                      >
                        <q-popup-proxy
                          transition-show="scale"
                          transition-hide="scale"
                        >
                          <q-time
                            :model-value="date.formatDate(adjustedShowingTime, 'YYYY-MM-DDTHH:mm:ssZ')"
                            flat
                            mask="YYYY-MM-DDTHH:mm:ssZ"
                            @update:model-value="value => newTime = date.extractDate(value, 'YYYY-MM-DDTHH:mm:ssZ')"
                          />
                        </q-popup-proxy>
                      </q-icon>
                    </template>
                  </q-input>
                </template>
                <template v-if="showing.action === SHOWING_CHANGE_ACTIONS.SCREEN_SWAP">
                  <q-input
                    class="col-6"
                    :model-value="showing.screen.name"
                    label="From Screen"
                    readonly
                  />
                  <q-select
                    v-model="newScreen"
                    class="col-6"
                    label="To Screen"
                    :options="screens.data"
                    :rules="[val => !!val || 'Selection is required']"
                    option-label="name"
                    :display-value="newScreen && newScreen.name"
                    bottom-slots
                  />
                  <seat-chart
                    v-if="seatSwapsRequired"
                    ref="originalSeatChart"
                    class="col-6 q-pt-none"
                    :seat-chart="seatChartForShowing"
                    :seat-chart-configuration="seatChartForShowing.seatChartConfiguration"
                    :seats-to-select="0"
                    :initial-selected-seats="[]"
                    :show-header="false"
                    :show-footer="false"
                    :show-seat-numbers="!!newScreenSeatChart"
                    :seat-swap-mode="true"
                    :source-seats="sourceSeats"
                  />
                  <div class="col-6 q-pt-none q-mb-lg relative-position">
                    <seat-chart
                      v-if="seatSwapsRequired && newScreenSeatChart"
                      ref="seatChart"
                      :seat-chart="newScreenSeatChart"
                      :seat-chart-configuration="newScreenSeatChart.seatChartConfiguration"
                      :seat-preferences="seatPreferences"
                      :seats-to-select="seatsToSelect"
                      :ticket-type-id="ticketTypeId"
                      :initial-selected-seats="overrideOrderAndTicketsSeats || []"
                      :show-header="false"
                      :show-footer="false"
                      :show-seat-numbers="true"
                      :seat-swap-mode="true"
                      :consolidate-to-center="consolidateToCenter"
                      :original-seats="originalSeats"
                      @update:model-value="assignSeats"
                      @reserved-seat-click="setOrderOverride"
                      @no-best-available="queueCancelOrder"
                    />
                    <div
                      v-if="$refs.seatChart"
                      class="col-6 text-center text-negative absolute"
                      style="bottom: -24px; left: 0px; right: 0px;"
                    >
                      {{ $refs.seatChart.error }}
                    </div>
                  </div>
                </template>
              </div>
              <div
                v-if="showing.action === SHOWING_CHANGE_ACTIONS.SCREEN_SWAP && seatSwapsRequired && newScreenSeatChart"
                class="row q-mt-none q-col-gutter-lg"
              >
                <q-toggle
                  v-model="overrideSeatSwaps"
                  class="col-6 q-pt-md"
                  label="Override Seat Swaps"
                >
                  <q-tooltip>
                    Manually override seat swaps for one or more orders
                  </q-tooltip>
                </q-toggle>
                <div
                  v-if="overrideSeatSwaps && overrideOrderAndTicketsIndex !== null"
                  class="col-6 q-pt-none row items-center justify-center relative-position"
                >
                  <div class="column text-center">
                    <div>Order #{{ overrideOrderAndTickets[0].id }}</div>
                    <div>({{ overrideOrderAndTicketsIndex + 1 }} of {{ ordersAndTickets.length }})</div>
                  </div>
                  <q-icon
                    style="left: 20px"
                    class="absolute cursor-pointer"
                    size="md"
                    name="arrow_back"
                    @click="cycleOrderAndTickets(-1)"
                  />
                  <q-icon
                    class="absolute cursor-pointer"
                    style="right: 0"
                    size="md"
                    name="arrow_forward"
                    @click="cycleOrderAndTickets(1)"
                  />
                </div>
              </div>
              <div
                v-if="showing.action === SHOWING_CHANGE_ACTIONS.SCREEN_SWAP && seatSwapsRequired && newScreenSeatChart"
                class="row items-start q-pt-lg"
              >
                <div class="row q-pb-md">
                  Seats highlighted in green will be swapped based on the user's
                  seat preferences (if available) or their current seats. The seat
                  chart for the destination screen (on the right) shows the
                  original seat numbers for reference.
                </div>
                <div class="row">
                  Seats highlighted in red will be cancelled due to insufficient availability.
                </div>
              </div>
            </q-step>

            <q-step
              v-if="ordersAndTickets.length"
              :name="2"
              title="Notify"
              icon="campaign"
              :done="step > 2"
              style="min-height: 400px;"
            >
              <template v-if="showing.action === SHOWING_CHANGE_ACTIONS.SCREEN_SWAP">
                <div class="row q-mb-md">
                  <q-input
                    v-model="screenSwapMessage"
                    class="col-12"
                    type="textarea"
                    label="Message to customers whose seats will change"
                    :disable="!sendScreenSwapMessage"
                  />
                  <q-toggle
                    v-model="sendScreenSwapMessage"
                    class="col-12"
                    label="Send Screen Swap Message"
                    bottom-slots
                  />
                </div>
              </template>
              <template v-if="showing.action === SHOWING_CHANGE_ACTIONS.RESCHEDULE">
                <div class="row q-mb-md">
                  <q-input
                    v-model="rescheduleMessage"
                    class="col-12"
                    type="textarea"
                    label="Message to customers whose film will be rescheduled"
                    :disable="!sendRescheduleMessage"
                  />
                  <q-toggle
                    v-model="sendRescheduleMessage"
                    class="col-12"
                    label="Send Rescheduled Message"
                    bottom-slots
                  />
                </div>
              </template>
              <template v-if="showing.action === SHOWING_CHANGE_ACTIONS.CANCEL || showing.action === SHOWING_CHANGE_ACTIONS.SCREEN_SWAP">
                <div class="row q-mb-md">
                  <q-input
                    v-model="cancelMessage"
                    class="col-12"
                    type="textarea"
                    label="Message to customers whose tickets will be cancelled"
                    :disable="!cancelledTicketCount"
                  />
                </div>
                <div class="row q-col-gutter-md q-ml-xs q-my-md">
                  <q-table
                    class="full-width"
                    :columns="[
                      { name: 'name', align: 'left', label: 'Refund Type', field: 'name' },
                      { name: 'description', align: 'left', label: 'Description', field: 'description' },
                      { name: 'enabled', label: 'Enabled', component: 'checkbox', field: 'enabled' }
                    ]"
                    :rows="refundTypeConfiguration"
                    hide-pagination
                    bordered
                    flat
                  >
                    <template #top>
                      <div class="q-table__title">
                        Refund Type Configuration
                      </div>

                      <q-space />

                      <q-btn
                        class="q-mx-sm"
                        label="Set Order"
                        @click="$refs.orderDialog.show()"
                      />
                    </template>
                    <template #body-cell="props">
                      <q-td :props="props">
                        <template v-if="props.col.component === 'checkbox'">
                          <q-checkbox
                            v-model="props.row[props.col.field]"
                            :disable="props.row.isDefault"
                          />
                        </template>
                        <template v-else>
                          {{ props.row[props.col.field] }}
                        </template>
                      </q-td>
                    </template>
                    <template #bottom>
                      <div class="text-subtitle1">
                        <span>Refunds will be attempted in the following order: &nbsp;[</span>
                        <span
                          v-for="(refundOption, index) in $_.filter(refundTypeConfiguration, option => option.enabled)"
                          :key="refundOption.value"
                        >
                          {{ index > 0 ? ' &nbsp;->&nbsp;' : '' }}
                          {{ refundOption.name }}
                        </span>
                        ]
                      </div>
                    </template>
                  </q-table>
                  <q-checkbox
                    v-if="currentCircuit.enableSms"
                    v-model="sendEmail"
                    class="col-3"
                    label="Send Email"
                  >
                    <q-tooltip>
                      Send an email notification to impacted customers.
                    </q-tooltip>
                  </q-checkbox>
                  <q-checkbox
                    v-if="currentCircuit.enableSms"
                    v-model="sendSms"
                    class="col-3"
                    label="Send SMS"
                  >
                    <q-tooltip>
                      Send an SMS notification to impacted customers. This requires a workflow for the "Showing Change Event" in CustomerIO.
                    </q-tooltip>
                  </q-checkbox>
                </div>
                <div class="row q-mb-md">
                  <div class="q-mt-md">
                    {{ cancelledTicketCount }} customers will have their tickets cancelled.
                  </div>
                </div>
              </template>
            </q-step>

            <q-step
              :name="3"
              title="Review"
              icon="publish"
              style="min-height: 400px;"
            >
              <template v-if="showing.action === SHOWING_CHANGE_ACTIONS.SCREEN_SWAP">
                <div class="row q-mb-md">
                  The showing is being moved from {{ showing.screen.name }} to {{ newScreen && newScreen.name }}.
                </div>
                <div
                  v-if="ordersAndTickets.length"
                  class="q-mb-md"
                >
                  <div class="row">
                    {{ sendScreenSwapMessage ? 'Screen swap notification message:' : 'Screen swap notifications are disabled.' }}
                  </div>
                  <div
                    v-if="sendScreenSwapMessage"
                    class="row text-italic"
                  >
                    {{ screenSwapMessage }}
                  </div>
                </div>
                <div class="row q-mb-md">
                  {{ cancelledTicketCount }} customers will have their tickets cancelled.
                </div>
                <div
                  v-if="cancelledTicketCount > 0"
                  class="q-mb-md"
                >
                  <div class="row">
                    Cancellation notification message:
                  </div>
                  <div
                    class="row text-italic"
                  >
                    {{ cancelMessage }}
                  </div>
                </div>
              </template>

              <template v-if="showing.action === SHOWING_CHANGE_ACTIONS.RESCHEDULE">
                <div class="row q-mb-md">
                  The showing is being moved from {{ date.formatDate(showing.siteTime, 'YYYY MMM D, h:mm a') }} to {{ date.formatDate(newTime, 'YYYY MMM D, h:mm a') }}.
                </div>
                <div
                  v-if="ordersAndTickets.length"
                  class="q-mb-md"
                >
                  <div class="row">
                    {{ sendRescheduleMessage ? 'Reschedule notification message:' : 'Reschedule notifications are disabled.' }}
                  </div>
                  <div
                    v-if="sendRescheduleMessage"
                    class="row text-italic"
                  >
                    {{ rescheduleMessage }}
                  </div>
                </div>
              </template>

              <template v-if="showing.action === SHOWING_CHANGE_ACTIONS.CANCEL">
                <div class="row q-mb-md">
                  The showing is being cancelled.
                </div>
                <div class="row q-mb-md">
                  {{ cancelledTicketCount }} customers will have their tickets cancelled.
                </div>
                <div
                  v-if="cancelledTicketCount > 0"
                  class="q-mb-md"
                >
                  <div class="row">
                    Cancellation notification message:
                  </div>
                  <div
                    class="row text-italic"
                  >
                    {{ cancelMessage }}
                  </div>
                </div>
              </template>
            </q-step>

            <template #navigation>
              <q-stepper-navigation>
                <q-btn
                  color="primary"
                  :label="step === 3 ? 'Confirm' : 'Next'"
                  :disabled="disableNext()"
                  @click="next"
                />
                <q-btn
                  v-if="step > 1"
                  flat
                  color="secondary"
                  label="Back"
                  class="q-ml-sm"
                  @click="back"
                />
                <q-btn
                  flat
                  color="negative"
                  label="Cancel"
                  class="q-ml-sm"
                  @click="reset"
                />
              </q-stepper-navigation>
            </template>

            <template #message>
              <q-banner
                v-if="step === 1"
                class="bg-purple-8 text-white q-px-lg"
              >
                Select the type of change you'd like to make.
              </q-banner>
              <q-banner
                v-else-if="step === 2"
                class="bg-orange-8 text-white q-px-lg"
              >
                Customise the notifications that your customers will receive.
              </q-banner>
              <q-banner
                v-else-if="step === 3"
                class="bg-green-8 text-white q-px-lg"
              >
                Review and commit your changes.
              </q-banner>
            </template>
          </q-stepper>
        </q-card-section>
      </template>
    </q-card>
    <set-display-order-dialog
      ref="orderDialog"
      :list="refundTypeConfiguration"
      @reorder="handleReorder"
    />
  </q-dialog>
</template>

<script>
import { date, Notify } from 'quasar'
import Showing, { SHOWING_CHANGE_ACTIONS } from 'src/models/showing/Showing'
import SetDisplayOrderDialog from 'src/modules/admin/components/SetDisplayOrderDialog'
import SeatChart from 'src/modules/admin/seat-chart/SeatChart'

export const CANCEL_USER_REFUND_TYPES = {
  CREDIT: 'credit',
  CARD: 'card',
  UNCLAIMED_REFUND: 'unclaimed-refund'
}

const SWAP_STRATEGIES = {
  MAGIC: 'magic',
  ORIGINAL_SEATS: 'original-seats',
  USER_PREFERENCE: 'user-preference'
}

SWAP_STRATEGIES.ALL = _.values(SWAP_STRATEGIES)

export default {
  name: 'ShowingChangeDialog',
  components: { SeatChart, SetDisplayOrderDialog },
  configuration: ['currentCircuit', 'currentSite', 'screens'],
  emits: ['change'],
  data () {
    return {
      shown: false,
      step: 1,
      showing: null,
      cancelMessage: 'Your film showing has been cancelled. Please contact the box office for more information.',
      rescheduleMessage: 'Your film showing has been rescheduled. Please contact the box office for more information.',
      sendRescheduleMessage: false,
      screenSwapMessage: 'Your film showing has been changed. Please contact the box office for more information.',
      sendScreenSwapMessage: false,
      sendEmail: true,
      sendSms: false,
      swapStrategy: SWAP_STRATEGIES.MAGIC,
      relocationTolerance: 0.5,
      consolidateToCenter: false,
      overrideSeatSwaps: false,
      overrideOrderAndTicketsIndex: null,
      originalSeats: null,
      sourceSeats: null,
      newScreen: null,
      newTime: null,
      newScreenSeatChart: null,
      ordersAndTicketsIndex: null,
      seatPreferences: undefined,
      seatsToSelect: 0,
      ticketTypeId: null,
      seatSwapMap: {},
      ordersAndTicketsQueuedForCancel: [],
      changeExecuting: false,
      refundTypeConfiguration: this.generateRefundTypeRows()
    }
  },
  computed: {
    originalSeatChartSeatArray () {
      // this returns the seat array for the original screen's seat chart
      // originalSeatChart is the vue component, seatChart(1) is its seatChart object and seatChart(2) is the array of seats
      return this.$refs.originalSeatChart.seatChart.seatChart
    },
    newSeatChartSeatArray () {
      // this returns the seat array for the new screen's seat chart
      // seatChart(1) is the vue component, seatChart(2) is its seatChart object and seatChart(3) is the array of seats
      return this.$refs.seatChart.seatChart.seatChart
    },
    seatSwapsRequired () {
      return this.seatChartForShowing && this.seatChartForShowing.isRemote && !!this.ordersAndTickets.length
    },
    adjustedShowingTime () {
      return this.newTime || this.showing?.siteTime
    },
    cancelledTicketCount () {
      if (this.showing?.action === SHOWING_CHANGE_ACTIONS.CANCEL) {
        return this.showing.ticketsSold
      } else if (this.showing?.action === SHOWING_CHANGE_ACTIONS.SCREEN_SWAP) {
        return _(this.ordersAndTicketsQueuedForCancel).map(([, tickets]) => tickets.length).push(0).sum()
      } else {
        return 0
      }
    },
    ordersAndTickets () {
      if (!this.showing) return []

      return _(this.showing.orders.data)
        .sortBy(({ createdAt }) => createdAt)
        .map(order => {
          const tickets = _(order.orderItems.data)
            .map('ticket')
            .compact()
            .filter(({ state, showing }) => (state === 'ticket' || state === 'reservation') && showing.id === this.showing.id)
            .value()
          return tickets.length
            ? [
                order,
                tickets
              ]
            : null
        })
        .compact()
        .value()
    },
    orderAndTickets () {
      return this.ordersAndTickets[this.ordersAndTicketsIndex]
    },
    overrideOrderAndTickets () {
      return this.ordersAndTickets[this.overrideOrderAndTicketsIndex]
    },
    overrideOrderAndTicketsSeats () {
      return _.map(this.overrideOrderAndTickets?.[1], ticket => this.seatSwapMap[ticket.seatName])
    },
    editingOrderAndTickets () {
      return this.overrideSeatSwaps ? this.overrideOrderAndTickets : this.orderAndTickets
    },
    headerHeight () {
      return (this.$refs.cardHeader?.$el.clientHeight || 0) + 'px'
    },
    numSeatsAvailableForTicketTypeOnOriginalSeatChart () {
      return _(this.showing.screen.seatChart.seatChart).flatten().filter(seat => {
        if (this.ticketTypeId && !_.isNil(seat.areaCategoryId)) {
          return _.includes(this.showing.seatTicketTypes, this.ticketTypeId)
        }

        return _.isNil(seat.areaCategoryId)
      }).value().length
    }
  },
  watch: {
    'newScreen.id' () {
      this.handleScreenSwap()
    },
    swapStrategy () {
      this.handleScreenSwap()
    },
    relocationTolerance () {
      this.handleScreenSwap()
    },
    consolidateToCenter () {
      this.handleScreenSwap()
    },
    overrideSeatSwaps () {
      if (this.overrideSeatSwaps) {
        this.overrideOrderAndTicketsIndex = 0
      } else {
        this.overrideOrderAndTicketsIndex = null
      }
    },
    overrideOrderAndTickets () {
      this.sourceSeats = this.overrideOrderAndTickets ? _.map(this.overrideOrderAndTickets[1], 'seatName') : null

      if (this.overrideOrderAndTickets) {
        this.handleManualSwap()
      } else {
        this.$refs.seatChart.clearSelectedSeats()
      }
    }
  },
  created () {
    _.extend(this, { date, SHOWING_CHANGE_ACTIONS, CANCEL_USER_REFUND_TYPES, SWAP_STRATEGIES })
  },
  models: {
    seatChartForShowing: {
      variables () {
        return {
          showingId: this.showing?.id,
          orderId: null,
          skip: !this.showing?.isRemote
        }
      },
      immediate: true,
      force: true
    }
  },
  methods: {
    generateRefundTypeRows () {
      const rows = []

      rows.push({ value: CANCEL_USER_REFUND_TYPES.CREDIT, name: 'User Credit', displayOrder: 1, description: 'If a user exists on the order, refund to user credit', enabled: true })
      rows.push({ value: CANCEL_USER_REFUND_TYPES.CARD, name: 'Card', displayOrder: 2, description: 'If order was paid by card, refund back to the card', enabled: true })
      rows.push({ isDefault: true, value: CANCEL_USER_REFUND_TYPES.UNCLAIMED_REFUND, name: 'Unclaimed Refund', displayOrder: 3, description: 'If no other refund options are available, cancel order and refund at the point of sale', enabled: true })

      return rows
    },
    handleReorder ({ reorderedList }) {
      this.refundTypeConfiguration = reorderedList
    },
    disableNext () {
      switch (this.showing?.action) {
        case SHOWING_CHANGE_ACTIONS.CANCEL:
          return false
        case SHOWING_CHANGE_ACTIONS.RESCHEDULE:
          return !this.newTime || this.newTime === this.showing.siteTime
        case SHOWING_CHANGE_ACTIONS.SCREEN_SWAP:
          if (this.seatSwapsRequired) {
            const actionRequiredForOrdersAndTickets = !!this.ordersAndTickets?.length && _.some(this.ordersAndTickets, ([order, tickets]) => {
              const orderNotYetQueuedForCancel = !_.find(this.ordersAndTicketsQueuedForCancel, ([queuedOrder]) => queuedOrder.id === order.id)
              const ticketNotYetQueuedForSwap = _.some(tickets, ticket => !this.seatSwapMap[ticket.seatName])
              return orderNotYetQueuedForCancel && ticketNotYetQueuedForSwap
            })
            return !this.newScreen || actionRequiredForOrdersAndTickets
          } else {
            return !this.newScreen || this.newScreen.name === this.showing.screen.name
          }
        default:
          return true
      }
    },
    cycleOrderAndTickets (indexOffset) {
      const nextOrderAndTicketsIndex = this.overrideOrderAndTicketsIndex + indexOffset

      if (nextOrderAndTicketsIndex < 0) {
        this.overrideOrderAndTicketsIndex = this.ordersAndTickets.length - 1
      } else if (nextOrderAndTicketsIndex >= this.ordersAndTickets.length) {
        this.overrideOrderAndTicketsIndex = 0
      } else {
        this.overrideOrderAndTicketsIndex = nextOrderAndTicketsIndex
      }
    },
    getPreferenceRatiosFromOriginalSeatChart (seatName) {
      const { rowIndex, colIndex } = this.$refs.originalSeatChart.seatLocation(seatName)
      const colCount = this.originalSeatChartSeatArray[0].length

      return {
        y_ratio: Math.round(((rowIndex / (this.originalSeatChartSeatArray.length - 1)) / 0.05)) * 0.05,
        ...(this.consolidateToCenter
          ? {}
          : {
              x_ratio: Math.round(((colIndex / (colCount - 1)) / 0.05)) * 0.05
            })
      }
    },
    getPreferenceRatiosFromSeatChart (seatName) {
      const { rowIndex, colIndex } = this.$refs.seatChart.seatLocation(seatName)
      const colCount = this.newSeatChartSeatArray[0].length
      return {
        x_ratio: Math.round(((colIndex / (colCount - 1)) / 0.05)) * 0.05,
        y_ratio: Math.round(((rowIndex / (this.newSeatChartSeatArray.length - 1)) / 0.05)) * 0.05
      }
    },
    getSeatPreferences (order, tickets) {
      const firstTicket = tickets[0]
      const preferenceRatios = this.getPreferenceRatiosFromOriginalSeatChart(firstTicket.seatName)

      if (this.swapStrategy === SWAP_STRATEGIES.ORIGINAL_SEATS) {
        return preferenceRatios
      }

      if (this.swapStrategy === SWAP_STRATEGIES.USER_PREFERENCE) {
        return {
          ...preferenceRatios,
          ...(order.user?.seatPreferences || {})
        }
      }

      const numReservedSeats = _(this.$refs.seatChart.computedSeatChart).flatten().filter('reserved').value().length
      const percentageSeatsSoldWhenOriginalTicketsBought = numReservedSeats / this.numSeatsAvailableForTicketTypeOnOriginalSeatChart

      if (percentageSeatsSoldWhenOriginalTicketsBought > 0.4) {
        // ORIGINAL SEAT SELECTION WAS FORCED, SO OVERRIDE WITH USER PREFERENCE
        return {
          ...preferenceRatios,
          ...(order.user?.seatPreferences || {})
        }
      } else {
        // PREFER PREFERENCE RATIOS FROM ORIGINAL SEAT SELECTION
        return {
          ...(order.user?.seatPreferences || {}),
          ...preferenceRatios
        }
      }
    },
    getOriginalSeats (tickets) {
      if (this.swapStrategy === SWAP_STRATEGIES.USER_PREFERENCE) return null

      const numReservedSeats = _(this.$refs.seatChart.computedSeatChart).flatten().filter('reserved').value().length
      const percentageSeatsSoldWhenOriginalTicketsBought = numReservedSeats / this.numSeatsAvailableForTicketTypeOnOriginalSeatChart
      const effectiveUserPreferenceSwapStrategy = this.swapStrategy === SWAP_STRATEGIES.MAGIC && percentageSeatsSoldWhenOriginalTicketsBought > 0.4

      const firstTicket = tickets[0]
      const preferenceRatiosForOriginalSeatChart = this.getPreferenceRatiosFromOriginalSeatChart(firstTicket.seatName)
      const preferenceRatiosForSeatChart = this.getPreferenceRatiosFromSeatChart(firstTicket.seatName)
      const seatsWithSameNameFallOutsideRelocationTolerance = (
        Math.abs(preferenceRatiosForOriginalSeatChart.y_ratio - preferenceRatiosForSeatChart.y_ratio) > this.relocationTolerance ||
        Math.abs(preferenceRatiosForOriginalSeatChart.x_ratio - preferenceRatiosForSeatChart.x_ratio) > this.relocationTolerance
      )

      // CHECK FOR MOVEMENT BEYOND TOLERANCE FROM ORIGINAL SEAT POSITION
      if (effectiveUserPreferenceSwapStrategy || seatsWithSameNameFallOutsideRelocationTolerance) {
        return null
      } else {
        return _.map(tickets, 'seatName')
      }
    },
    async handleScreenSwap () {
      this.overrideSeatSwaps = false
      this.newScreenSeatChart?.destroy()
      this.seatChartForShowing.isDirty && this.seatChartForShowing.revert()

      const firstOrderAndTickets = this.ordersAndTickets[0]

      if (this.seatSwapsRequired && this.newScreen && this.newScreen.seatChart && firstOrderAndTickets) {
        const [order, tickets] = firstOrderAndTickets
        const firstTicket = tickets[0]
        this.newScreenSeatChart = this.newScreen.seatChart.clone()
        this.ordersAndTicketsIndex = 0

        this.seatsToSelect = tickets.length
        this.ticketTypeId = firstTicket.ticketType.appliesToSpecificSeats ? firstTicket.ticketType.id : null

        await this.$nextTick()

        // COMPUTE THESE AFTER SETTING THE TICKET TYPE ID
        this.seatPreferences = this.getSeatPreferences(order, tickets)

        this.originalSeats = this.getOriginalSeats(tickets)

        this.seatSwapMap = {}
        this.ordersAndTicketsQueuedForCancel = []

        // TRIGGER THE FIRST ORDER AUTOSELECT
        await this.$nextTick()
        this.$refs.seatChart.autoSelect()
      } else {
        this.newScreenSeatChart = null
        this.ordersAndTicketsIndex = null
        this.seatPreferences = undefined
        this.originalSeats = null
        this.seatsToSelect = 0
        this.ticketTypeId = null
        this.seatmap = {}
        this.ordersAndTicketsQueuedForCancel = []

        if (!this.newScreen) return

        const newCapacity = this.newScreen && this.newScreen.seatCount - this.newScreen.heldSeatCount
        const oversold = this.newScreen && newCapacity < this.showing.ticketsSold

        if (oversold) {
          const ordersAndTicketsOrderedByMostRecent = this.ordersAndTickets.reverse()
          const oversoldBy = this.showing.ticketsSold - newCapacity

          let index = 0
          let cancelledCount = 0

          while (cancelledCount < oversoldBy) {
            const mostRecentOrderAndTickets = ordersAndTicketsOrderedByMostRecent[index]
            const [, mostRecentTickets] = mostRecentOrderAndTickets
            this.ordersAndTicketsQueuedForCancel.push(mostRecentOrderAndTickets)
            cancelledCount += mostRecentTickets.length
            index++
          }
        }
      }
    },
    async queueNextSwap () {
      const nextOrdersAndTicketsIndex = this.ordersAndTicketsIndex + 1
      const nextOrderAndTickets = this.ordersAndTickets[nextOrdersAndTicketsIndex]

      if (!nextOrderAndTickets) {
        this.ordersAndTicketsIndex = null
        this.seatPreferences = undefined
        this.originalSeats = null
        this.seatsToSelect = 0
        this.ticketTypeId = null
        this.$refs.seatChart.clearSelectedSeats()
        return
      }

      const [nextOrder, nextTickets] = nextOrderAndTickets
      const firstTicket = nextTickets[0]

      this.ordersAndTicketsIndex = nextOrdersAndTicketsIndex

      this.seatsToSelect = nextTickets.length
      this.ticketTypeId = firstTicket.ticketType.appliesToSpecificSeats ? firstTicket.ticketType.id : null

      // COMPUTE THESE AFTER SETTING THE TICKET TYPE ID
      this.seatPreferences = this.getSeatPreferences(nextOrder, nextTickets)

      this.originalSeats = this.getOriginalSeats(nextTickets)

      await this.$nextTick()

      // TRIGGER THE NEXT ORDER AUTOSELECT
      this.$refs.seatChart.clearSelectedSeats()
      this.$refs.seatChart.autoSelect()
    },
    async handleManualSwap () {
      const [order, tickets] = this.overrideOrderAndTickets
      const firstTicket = tickets[0]

      this.seatsToSelect = tickets.length
      this.ticketTypeId = firstTicket.ticketType.appliesToSpecificSeats ? firstTicket.ticketType.id : null

      // COMPUTE THESE AFTER SETTING THE TICKET TYPE ID
      this.seatPreferences = this.getSeatPreferences(order, tickets)

      await this.$nextTick()

      this.$refs.seatChart.setInternalSelectedSeats()
    },
    async assignSeats ({ selectedSeats, complete }) {
      if (complete) {
        const editingOrderAndTickets = this.editingOrderAndTickets

        if (!editingOrderAndTickets) return

        const [order, tickets] = editingOrderAndTickets

        _.each(tickets, (ticket, ticketIndex) => {
          const seatName = selectedSeats[ticketIndex]

          if (this.seatSwapMap[ticket.seatName]) {
            /**
             * If we already had a swapped seat, mark it as available on the seat chart.
             */
            const originalSeatLocations = this.$refs.seatChart.seatLocations(this.seatSwapMap[ticket.seatName])
            _.each(originalSeatLocations, originalSeatLocation => {
              const { colIndex, rowIndex } = originalSeatLocation
              this.newScreenSeatChart.seatChart[rowIndex][colIndex].reserved = false
              this.newScreenSeatChart.seatChart[rowIndex][colIndex].available = true
            })
          }

          const seatLocations = this.$refs.seatChart.seatLocations(seatName)
          _.each(seatLocations, seatLocation => {
            const { colIndex, rowIndex } = seatLocation
            this.newScreenSeatChart.seatChart[rowIndex][colIndex].reserved = true
            this.newScreenSeatChart.seatChart[rowIndex][colIndex].available = false
          })

          this.seatSwapMap[ticket.seatName] = seatName
        })

        /**
         * If we've previous queued the order to be cancelled, but we've now
         * found seats for it, remove it from the cancel queue.
         */
        if (_.some(this.ordersAndTicketsQueuedForCancel, ([orderQueuedForCancel]) => {
          return order.id === orderQueuedForCancel.id
        })) {
          this.ordersAndTicketsQueuedForCancel = _.filter(this.ordersAndTicketsQueuedForCancel, ([orderQueuedForCancel]) => {
            return order.id !== orderQueuedForCancel.id
          })
        }

        if (!this.overrideSeatSwaps || !this.overrideOrderAndTickets) {
          this.queueNextSwap()
        }
      }
    },
    async queueCancelOrder () {
      const currentOrderAndTickets = this.ordersAndTickets[this.ordersAndTicketsIndex]
      this.ordersAndTicketsQueuedForCancel.push(currentOrderAndTickets)

      const [, currentTickets] = currentOrderAndTickets

      _.each(currentTickets, ticket => {
        const originalSeatLocations = this.$refs.originalSeatChart.seatLocations(ticket.seatName)
        _.each(originalSeatLocations, originalSeatLocation => {
          const { colIndex, rowIndex } = originalSeatLocation
          this.seatChartForShowing.seatChart[rowIndex][colIndex].reserved = false
          this.seatChartForShowing.seatChart[rowIndex][colIndex].cancelled = true
        })
      })

      this.queueNextSwap()
    },
    setOrderOverride (seatName) {
      if (!this.overrideSeatSwaps) return

      _.some(this.ordersAndTickets, ([, tickets], index) => {
        return _.some(tickets, ticket => {
          if (seatName === this.seatSwapMap[ticket.seatName]) {
            this.overrideOrderAndTicketsIndex = index
            return true
          }
        })
      })
    },
    async executeShowingChange () {
      const config = {}

      switch (this.showing.action) {
        case SHOWING_CHANGE_ACTIONS.CANCEL:
          config.orders_to_cancel = _.map(this.ordersAndTickets, ([order]) => order.id)
          config.cancel_message = this.cancelMessage
          config.refund_payment_types = _(this.refundTypeConfiguration).filter('enabled').map('value').value()
          config.send_email = this.sendEmail
          config.send_sms = this.sendSms
          break
        case SHOWING_CHANGE_ACTIONS.RESCHEDULE:
          config.time = this.newTime
          config.reschedule_message = this.rescheduleMessage
          config.send_reschedule_message = this.sendRescheduleMessage
          config.send_email = this.sendEmail
          config.send_sms = this.sendSms
          break
        case SHOWING_CHANGE_ACTIONS.SCREEN_SWAP:
          config.screen_id = this.newScreen.id
          config.tickets = this.seatSwapMap
          config.orders_to_cancel = _.map(this.ordersAndTicketsQueuedForCancel, ([order]) => order.id)
          config.cancel_message = this.cancelMessage
          config.refund_payment_types = _(this.refundTypeConfiguration).filter('enabled').map('value').value()
          config.screen_swap_message = this.screenSwapMessage
          config.send_screen_swap_message = this.sendScreenSwapMessage
          config.send_email = this.sendEmail
          config.send_sms = this.sendSms
          break
      }

      const notificationMessage = `Showing ${this.showing.action === SHOWING_CHANGE_ACTIONS.CANCEL ? 'cancelled' : 'updated'}.`

      this.changeExecuting = true
      await this.showing.executeShowingChange(config)
      this.changeExecuting = false

      Notify.create({
        message: notificationMessage,
        type: 'positive',
        position: 'top'
      })

      this.$emit('change')
      this.reset()
    },
    async show (showing) {
      this.shown = true
      this.showing = new Showing({ id: showing.id }, {
        orders: {
          user: {
            seatPreferences: true
          },
          orderItems: {
            ticket: {
              fields: 'all',
              showing: {
                movie: 'default'
              }
            }
          }
        },
        movie: true
      })
      await this.showing.fetch()
    },
    next () {
      if (this.step === 3) {
        this.executeShowingChange()
      } else {
        this.$refs.stepper.next()
      }
    },
    back () {
      this.$refs.stepper.previous()
    },
    reset () {
      this.showing.isDirty && this.showing.revert()
      this.seatChartForShowing.isDirty && this.seatChartForShowing.revert()
      this.newScreenSeatChart?.destroy()
      Object.assign(this.$data, this.$options.data.apply(this))
    }
  }
}
</script>
